import React, { useCallback } from 'react'
// @ts-ignore no types
import analytics from '@adc/analytics'
import * as blueprint from '@adc/blueprint'
import * as graph from '@adc/sdk'
import { Placeholder } from 'semantic-ui-react'
import _ from 'lodash'
import type { Listing, Property, CurrentTargetAudience } from '@adc/sdk/types'
import classnames from 'classnames/bind'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import Button from '../../elementals/button'
import { adglareHtmlOverride } from './fixture'
import {
  ListingCardLinkProps,
  AddressInfoLine,
  CardImage,
  PropertyInfo,
  PropertyValue,
  ListingCardIndicator,
  LobIndicatorRow,
  buildSiblingListingPaths,
} from './cardParts'
import cls from './styles.scss'
import { getV2ApplicableBadges } from '../badges'
import { windowSizes } from '../../../constants'

const cx = classnames.bind(cls)

// Enum definition
export enum ListingCardViewModeEnum {
  HORIZONTAL = 'horizontal',
  GRID = 'grid',
  DEFAULT = 'default',
}

interface InjectedContentButton {
  text: string
  type: string
  link: string
  className?: string
  role?: string
  elmId?: string
  'aria-label'?: string
}

interface InjectedContentConfiguration {
  htmlOverride?: string
  main_photo?: string
  main_text?: string
  sub_text?: string
  buttons?: InjectedContentButton[]
}

interface ListingCardProps {
  className?: string
  elmId?: string
  listing: Listing & {
    injectedContentConfiguration?: InjectedContentConfiguration
  }
  showSave?: boolean
  showAdContent?: boolean
  isSaved?: boolean
  onSaveToggle?: (property: Property, trackingContext: any) => Promise<void>
  linkProps?: ListingCardLinkProps & {
    target?: string
    rel?: string
  }
  viewMode?: ListingCardViewModeEnum
  loading?: boolean
  featureFlags?: Record<string, any>
  isFocused?: boolean
  verifiedImage?: string
  updateVerifiedImage?: (listingId: string, imageUrl: string | null) => void
  viewingFrom?: CurrentTargetAudience
  isCompact?: boolean
}

export interface ListingCardAnalyticsAttributes {
  badges: string[]
  isReserveMetShown: boolean
  displayedCurrentBid: number | null | undefined
  displayedERV: number | null | undefined
}

const defaultAdglareConfiguration: InjectedContentConfiguration = {
  htmlOverride: adglareHtmlOverride,
}

const defaultOnSaveToggle = async () => {}
const defaultOnClick = () => {}
const defaultOnMouseLeave = () => {}

const ListingCard = ({
  className = '',
  elmId = 'listing_card',
  // @ts-ignore adding default to empty object for compatibility reasons
  listing = {},
  showSave = true,
  isSaved,
  onSaveToggle = defaultOnSaveToggle,
  linkProps = {
    onClick: defaultOnClick,
    onMouseLeave: defaultOnMouseLeave,
    href: undefined,
  },
  viewMode = ListingCardViewModeEnum.DEFAULT,
  loading = false,
  showAdContent = false,
  featureFlags = {},
  isFocused = false,
  verifiedImage,
  updateVerifiedImage,
  viewingFrom = graph.enums.CurrentTargetAudience.Buyer,
  isCompact = false,
}: ListingCardProps) => {
  const onMouseLeave = () => {
    linkProps?.onMouseLeave && linkProps.onMouseLeave()
  }

  const stopPropagationClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const getMaxBadges = (): number => {
    let maxBadges = 3
    if (viewMode === ListingCardViewModeEnum.HORIZONTAL) {
      maxBadges = window.innerWidth < windowSizes.MEDIUM_WIDTH ? 1 : 2
    }
    return maxBadges
  }

  const getVisibleBadgesLabels = (): string[] =>
    getV2ApplicableBadges(listing, getMaxBadges())
      .map((badge: any) => badge.titleV2)
      .value()

  const getV2AnalyticsAttributes = (
    listing: Listing
  ): ListingCardAnalyticsAttributes => {
    return {
      badges: getVisibleBadgesLabels(),
      isReserveMetShown:
        blueprint.listing.utils.card.shouldDisplayReserveMet(listing),
      displayedCurrentBid: blueprint.listing.utils.card.shouldDisplayCurrentBid(
        listing
      )
        ? blueprint.listing.utils.card.getCurrentBid(listing)
        : null,
      displayedERV: blueprint.listing.utils.card.getERV(listing),
    }
  }

  const onMouseClick = () => {
    const trackingInfo = constructTrackingContext()
    linkProps.onClick?.(trackingInfo)
  }

  const constructTrackingContext = () => {
    const trackingInfo = {
      ...analytics.standardTracking.listing(listing),
      ...getV2AnalyticsAttributes(listing),
    }

    return trackingInfo
  }

  const debounceOnSave = useCallback(
    _.debounce(
      async (property: Property) =>
        await onSaveToggle?.(property, constructTrackingContext()),
      500,
      { leading: true, trailing: false }
    ),
    [onSaveToggle]
  )

  const handleSaveToggle = () => {
    const property = listing.primary_property
    debounceOnSave(property)
  }

  const renderPlaceholder = () => (
    <div className={cx('property-value-container')}>
      <Placeholder style={{ margin: 12, width: '100%' }}>
        <Placeholder.Header>
          <Placeholder.Line length='short' />
        </Placeholder.Header>
        <Placeholder.Paragraph style={{ width: '100%' }}>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
      </Placeholder>
    </div>
  )

  const isCancelled =
    listing.listing_status === graph.enums.ListingStatus.Canceled

  const isHorizontalView = viewMode === ListingCardViewModeEnum.HORIZONTAL

  const { injectedContentConfiguration = defaultAdglareConfiguration } = listing

  const renderInjectedCardContent = () => {
    if (injectedContentConfiguration.htmlOverride) {
      return (
        <div
          id='injectable-html-override-target'
          data-elm-id='injectable-html-override-target'
          className={cx('html-override-container')}
          dangerouslySetInnerHTML={{
            __html: injectedContentConfiguration.htmlOverride,
          }}
        />
      )
    } else {
      return (
        <>
          {' '}
          <div className={cx('property-card-image-container')}>
            <span
              className={cx('property-card-image-background')}
              style={{
                backgroundImage: `url(${injectedContentConfiguration.main_photo})`,
              }}
              role='img'
              aria-label='current-campaign-image'
            />
          </div>
          <div className={cx('injected-content-body')}>
            <div className={cx('header-text')}>
              {injectedContentConfiguration.main_text}
            </div>
            {injectedContentConfiguration.sub_text && (
              <div className={cx('sub-header-text')}>
                {injectedContentConfiguration.sub_text}
              </div>
            )}
            {injectedContentConfiguration.buttons &&
              injectedContentConfiguration.buttons.length > 0 && (
                <div>
                  {injectedContentConfiguration.buttons.map((button, index) => (
                    <a
                      key={index}
                      href={button.link}
                      className={cx(button.className || '')}
                      role={button.role}
                      id={button.elmId}
                    >
                      <Button
                        size='small'
                        role={button.role as any}
                        elmId={`${button.elmId}_button`}
                        aria-label={button['aria-label']}
                      >
                        {button.text}
                      </Button>
                    </a>
                  ))}
                </div>
              )}
          </div>
        </>
      )
    }
  }

  return (
    <div
      className={cx(
        'root',
        { 'map-card': isHorizontalView },
        { 'grid-card': viewMode === ListingCardViewModeEnum.GRID },
        { 'compact-card': isCompact },
        { 'root-card-disabled': isCancelled },
        className
      )}
      onMouseLeave={onMouseLeave}
    >
      <a
        href={isCancelled || showAdContent ? undefined : linkProps?.href}
        className={cx('card-link')}
        onClick={onMouseClick}
        target={linkProps?.target}
        rel={linkProps?.rel}
      >
        <div
          className={cx(
            'base-card',
            {
              seen: listing.primary_property?.current_user_tracking_state
                ?.is_seen,
            },
            {
              focused: isFocused,
            },
            className
          )}
          data-elm-id={`property_card_${elmId}`}
        >
          {showAdContent ? (
            renderInjectedCardContent()
          ) : (
            <>
              <CardImage
                listing={listing}
                isHorizontalView={isHorizontalView}
                loading={loading}
                maxBadges={getMaxBadges()}
                featureFlags={featureFlags}
                verifiedImage={verifiedImage}
                updateVerifiedImage={updateVerifiedImage}
                viewingFrom={viewingFrom}
                isCompact={isCompact}
              />
              {loading ? (
                renderPlaceholder()
              ) : (
                <div className={cx('property-card-body')}>
                  <PropertyValue
                    elmId={elmId}
                    featureFlags={featureFlags}
                    isCancelled={isCancelled}
                    isSaved={isSaved}
                    listing={listing}
                    onSaveToggle={isCancelled ? _.noop : handleSaveToggle}
                    showSave={showSave && !showAdContent}
                    stopPropagationClick={stopPropagationClick}
                  />
                  <PropertyInfo
                    listing={listing}
                    elmId={elmId}
                    isCancelled={isCancelled}
                  />
                  <AddressInfoLine
                    address={listing.formatted_address}
                    elmId={elmId}
                    isCancelled={isCancelled}
                  />
                  {buildSiblingListingPaths(listing).length > 1 ? (
                    <LobIndicatorRow listing={listing} elmId={elmId} />
                  ) : (
                    <ListingCardIndicator
                      listing={listing}
                      elmId={elmId}
                      className={cx('listing-card-asset-info')}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </a>
    </div>
  )
}

// does not include fields required for badges which will have to be included separately
// @ts-ignore next line
ListingCard[GRAPH_API_FRAGMENT_DEFINITION] =
  blueprint.listing.fragments.listingCard.definition

// @ts-ignore next line
ListingCard[GRAPH_API_FRAGMENT_REFERENCE] =
  blueprint.listing.fragments.listingCard.reference

export default ListingCard
